import { toast } from 'sonner';

/**
 * Reliable share helper that works everywhere — including iframes
 * (Emergent preview, embedded contexts) where navigator.share AND
 * navigator.clipboard both reject with NotAllowedError.
 *
 * Strategy:
 *  1. Try `navigator.share` (mobile native sheet).
 *  2. On rejection (NotAllowedError / iframe) or unsupported, try
 *     `navigator.clipboard.writeText` (modern desktop).
 *  3. On clipboard rejection, fall back to a transient textarea +
 *     `document.execCommand('copy')` — this works inside iframes and
 *     older browsers without any permission prompts.
 *  4. Toast feedback on every outcome (success or failure).
 *  5. Silently no-op on AbortError (user dismissed the native sheet).
 *
 * @param {{url?: string, title?: string, text?: string}} opts
 * @returns {Promise<'shared'|'copied'|'failed'|'dismissed'>}
 */
export async function shareOrCopy({ url, title, text } = {}) {
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  if (!shareUrl) {
    toast.error('Nothing to share');
    return 'failed';
  }

  // Step 1: try native share
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({
        ...(title ? { title } : {}),
        ...(text ? { text } : {}),
        url: shareUrl,
      });
      return 'shared';
    } catch (err) {
      if (err && err.name === 'AbortError') return 'dismissed';
      // fall through to clipboard
    }
  }

  // Step 2 + 3: copy to clipboard with fallback
  const copied = await copyToClipboard(shareUrl);
  if (copied) {
    toast.success('Link copied to clipboard');
    return 'copied';
  }
  toast.error('Could not share — please copy the URL manually');
  return 'failed';
}

/**
 * Cross-context clipboard write. Returns true on success.
 * Tries navigator.clipboard first, then a hidden-textarea +
 * document.execCommand('copy') fallback that works in iframes.
 */
export async function copyToClipboard(text) {
  if (!text) return false;

  // Modern API (works on focused tabs, https origins)
  if (typeof navigator !== 'undefined' && navigator.clipboard && window.isSecureContext !== false) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to legacy
    }
  }

  // Legacy fallback — works in iframes and unfocused docs
  if (typeof document === 'undefined') return false;
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '0';
  textarea.style.opacity = '0';
  textarea.style.pointerEvents = 'none';
  document.body.appendChild(textarea);
  try {
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    return !!ok;
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
}
